/**
 * DataSourceSelector.js
 *
 * Multi-source field picker for the Data Explorer.
 * Renders collapsible panels per data source, each containing
 * checkboxes for individual fields.
 */

import React, { useMemo, useState } from 'react';
import { Collapse, Checkbox, Badge, Typography, Tag, Input } from 'antd';

const { Panel } = Collapse;

/**
 * Canonical data source → field mapping.
 * Keys MUST match the column aliases returned by the unified SQL endpoint.
 */
export const DATA_SOURCES = [
  {
    key: 'revenue',
    label: 'Revenue Analysis',
    color: '#0052CC',
    fields: [
      { key: 'gross_revenue', label: 'Gross Revenue' },
      { key: 'net_revenue', label: 'Net Revenue' },
      { key: 'net_revenue_post_dsm', label: 'Net Revenue Post DSM' },
      { key: 'revenue_charges', label: 'Revenue Charges' },
      { key: 'dsm_charges', label: 'DSM Charges' },
      { key: 'gdam_gross_rev', label: 'GDAM Gross Revenue' },
      { key: 'dam_gross_rev', label: 'DAM Gross Revenue' },
      { key: 'rtm_gross_rev', label: 'RTM Gross Revenue' },
      { key: 'gross_tariff', label: 'Gross Tariff' },
      { key: 'net_tariff', label: 'Net Tariff' },
      { key: 'actual_tariff', label: 'Actual Tariff' },
      { key: 'rev_total_payable', label: 'Revenue Total Payable' },
      { key: 'rev_total_receivable', label: 'Revenue Total Receivable' },
      { key: 'rev_total_penalty', label: 'Revenue Total Penalty' },
    ],
  },
  {
    key: 'volume',
    label: 'Volume Analysis',
    color: '#27AE60',
    fields: [
      { key: 'actual_generation', label: 'Actual Generation' },
      { key: 'scheduled_generation', label: 'Scheduled Generation' },
      { key: 'gdam_volume', label: 'GDAM Volume' },
      { key: 'dam_volume', label: 'DAM Volume' },
      { key: 'rtm_volume', label: 'RTM Volume' },
      { key: 'exchange_volume', label: 'Exchange Volume' },
      { key: 'total_cleared_vol_exbus', label: 'Total Cleared Vol Ex-Bus (MW)' },
      { key: 'gdam_vol_kwh', label: 'GDAM Vol (kWh)' },
      { key: 'dam_vol_kwh', label: 'DAM Vol (kWh)' },
      { key: 'rtm_vol_kwh', label: 'RTM Vol (kWh)' },
      { key: 'total_cleared_vol_kwh', label: 'Total Cleared Vol (kWh)' },
      { key: 'gdam_vol_kwh_exbus', label: 'GDAM Vol Ex-Bus (kWh)' },
      { key: 'dam_vol_kwh_exbus', label: 'DAM Vol Ex-Bus (kWh)' },
      { key: 'rtm_vol_kwh_exbus', label: 'RTM Vol Ex-Bus (kWh)' },
    ],
  },
  {
    key: 'dsm',
    label: 'DSM Analysis',
    color: '#E74C3C',
    fields: [
      { key: 'total_receivable', label: 'Total Receivable' },
      { key: 'total_payable', label: 'Total Payable' },
      { key: 'oi_penalty', label: 'OI Penalty' },
      { key: 'ui_penalty', label: 'UI Penalty' },
      { key: 'deviation_mw', label: 'Deviation (MW)' },
      { key: 'deviation_pct', label: 'Deviation (%)' },
      { key: 'dsm_net_payable', label: 'DSM Net Payable' },
      { key: 'dsm_avc', label: 'DSM Final AVC' },
      { key: 'dsm_final_schedule', label: 'DSM Final Schedule (MW)' },
      { key: 'dsm_actual_generation', label: 'DSM Actual Gen (MW)' },
      { key: 'dsm_wtd_avg_rate', label: 'DSM Wtd Avg Rate' },
      { key: 'deviation_kwh', label: 'Deviation (kWh)' },
      { key: 'dsm_total_penalty', label: 'DSM Total Penalty' },
      { key: 'oi_slab_1_unit', label: 'OI Slab 1 (0-12%)' },
      { key: 'oi_slab_2_unit', label: 'OI Slab 2 (12-15%)' },
      { key: 'oi_slab_3_unit', label: 'OI Slab 3 (15-20%)' },
      { key: 'oi_slab_4_unit', label: 'OI Slab 4 (>20%)' },
      { key: 'ui_slab_1_unit', label: 'UI Slab 1 (0-12%)' },
      { key: 'ui_slab_2_unit', label: 'UI Slab 2 (12-15%)' },
      { key: 'ui_slab_3_unit', label: 'UI Slab 3 (15-20%)' },
      { key: 'ui_slab_4_unit', label: 'UI Slab 4 (>20%)' },
    ],
  },
  {
    key: 'market',
    label: 'Market Analysis',
    color: '#8E44AD',
    fields: [
      { key: 'gdam_mcp', label: 'GDAM MCP' },
      { key: 'dam_mcp', label: 'DAM MCP' },
      { key: 'rtm_mcp', label: 'RTM MCP' },
      { key: 'gdam_mcv', label: 'GDAM MCV' },
      { key: 'dam_mcv', label: 'DAM MCV' },
      { key: 'rtm_mcv', label: 'RTM MCV' },
      { key: 'gross_rev_exch', label: 'Wtd Avg MCP' },
      { key: 'gross_rev_plant', label: 'Gross Tariff Plant' },
      { key: 'gross_rev_exchange', label: 'Gross Tariff Exchange' },
      { key: 'gdam_mcv_kwh', label: 'GDAM MCV (kWh)' },
      { key: 'dam_mcv_kwh', label: 'DAM MCV (kWh)' },
      { key: 'rtm_mcv_kwh', label: 'RTM MCV (kWh)' },
      { key: 'dam_revenue', label: 'DAM Revenue' },
      { key: 'stu_loss', label: 'STU Loss (%)' },
      { key: 'stu_charges', label: 'STU Charges Rate (Rs/kWh)' },
      { key: 'stu_charges_amount', label: 'STU Charges Amount' },
      { key: 'exchange_commission', label: 'Exchange Commission Rate' },
      { key: 'exchange_commission_amount', label: 'Exchange Commission Amount' },
      { key: 'trader_commission', label: 'Trader Commission Rate' },
      { key: 'trader_commission_amount', label: 'Trader Commission Amount' },
      { key: 'application_fees', label: 'Application Fees' },
      { key: 'tax_rate', label: 'Tax Rate (%)' },
      { key: 'tax_amount', label: 'Tax Amount' },
      { key: 'exch_total_charges', label: 'Exchange Total Charges' },
      { key: 'net_revenue_pre_dsm', label: 'Net Revenue Pre-DSM' },
    ],
  },
];

/** Flat lookup: field key → { sourceKey, label } */
export const FIELD_LOOKUP = (() => {
  const map = {};
  DATA_SOURCES.forEach(src =>
    src.fields.forEach(f => {
      map[f.key] = { sourceKey: src.key, sourceLabel: src.label, label: f.label, color: src.color };
    })
  );
  return map;
})();

/** Get all selected field keys as a flat array. */
export function getAllSelectedFieldKeys(selectedFields) {
  return Object.values(selectedFields).flat();
}

/**
 * DataSourceSelector component.
 *
 * @param {{ selectedFields: Object, onChange: Function }} props
 *   selectedFields: { revenue: ['gross_revenue', ...], volume: [...] }
 *   onChange: (newSelectedFields) => void
 */
export default function DataSourceSelector({ selectedFields, onChange }) {
  const [searchTerms, setSearchTerms] = useState({});

  // Count total selected
  const totalSelected = useMemo(
    () => Object.values(selectedFields).reduce((sum, arr) => sum + arr.length, 0),
    [selectedFields]
  );

  const handleFieldToggle = (sourceKey, fieldKey, checked) => {
    const current = selectedFields[sourceKey] || [];
    const updated = checked
      ? [...current, fieldKey]
      : current.filter(k => k !== fieldKey);

    const next = { ...selectedFields };
    if (updated.length === 0) {
      delete next[sourceKey];
    } else {
      next[sourceKey] = updated;
    }
    onChange(next);
  };

  const handleSourceToggleAll = (sourceKey, fields, checked) => {
    const next = { ...selectedFields };
    const term = (searchTerms[sourceKey] || '').toLowerCase();
    const targetFields = term
      ? fields.filter(f => f.label.toLowerCase().includes(term) || f.key.toLowerCase().includes(term))
      : fields;

    const targetKeys = targetFields.map(f => f.key);
    const currentSelected = selectedFields[sourceKey] || [];

    if (checked) {
      const updated = Array.from(new Set([...currentSelected, ...targetKeys]));
      next[sourceKey] = updated;
    } else {
      const updated = currentSelected.filter(k => !targetKeys.includes(k));
      if (updated.length === 0) {
        delete next[sourceKey];
      } else {
        next[sourceKey] = updated;
      }
    }
    onChange(next);
  };

  // Determine which panels should be open (those with selections)
  const activePanels = useMemo(
    () => Object.keys(selectedFields).filter(k => selectedFields[k]?.length > 0),
    [selectedFields]
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Text strong style={{ fontSize: 13, color: '#374151' }}>
          Data Sources
        </Typography.Text>
        {totalSelected > 0 && (
          <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
            {totalSelected} field{totalSelected !== 1 ? 's' : ''}
          </Tag>
        )}
      </div>

      <Collapse
        ghost
        size="small"
        defaultActiveKey={activePanels}
        style={{ background: 'transparent' }}
      >
        {DATA_SOURCES.map(source => {
          const selected = selectedFields[source.key] || [];

          // Filter fields based on search term
          const term = (searchTerms[source.key] || '').toLowerCase();
          const filteredFields = source.fields.filter(f =>
            f.label.toLowerCase().includes(term) || f.key.toLowerCase().includes(term)
          );

          const selectedInFiltered = selected.filter(k => filteredFields.some(ff => ff.key === k));
          const allChecked = filteredFields.length > 0 && selectedInFiltered.length === filteredFields.length;
          const indeterminate = selectedInFiltered.length > 0 && selectedInFiltered.length < filteredFields.length;

          return (
            <Panel
              key={source.key}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allChecked}
                    onChange={e => {
                      e.stopPropagation();
                      handleSourceToggleAll(source.key, source.fields, e.target.checked);
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{source.label}</span>
                  {selected.length > 0 && (
                    <Badge
                      count={selected.length}
                      size="small"
                      style={{ backgroundColor: source.color }}
                    />
                  )}
                </div>
              }
              style={{
                marginBottom: 4,
                borderRadius: 8,
                border: selected.length > 0 ? `1px solid ${source.color}30` : '1px solid #f0f0f0',
                background: selected.length > 0 ? `${source.color}06` : '#fafafa',
              }}
            >
              <div style={{ padding: '8px 12px 12px 12px' }}>
                <Input
                  placeholder={`Search ${source.label} fields...`}
                  value={searchTerms[source.key] || ''}
                  onChange={e => setSearchTerms({ ...searchTerms, [source.key]: e.target.value })}
                  style={{ marginBottom: 12, borderRadius: 6, maxWidth: 400 }}
                  size="small"
                  allowClear
                />
                {filteredFields.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: 12, paddingLeft: 8, fontStyle: 'italic' }}>
                    No fields found matching "{searchTerms[source.key]}"
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '8px 16px',
                    paddingLeft: 4,
                  }}>
                    {filteredFields.map(field => (
                      <Checkbox
                        key={field.key}
                        checked={selected.includes(field.key)}
                        onChange={e => handleFieldToggle(source.key, field.key, e.target.checked)}
                        style={{ fontSize: 12, margin: 0 }}
                      >
                        <span style={{ fontSize: 12, color: '#4b5563' }}>{field.label}</span>
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
}
